"use client";

import { useCallback, useMemo } from "react";
import type {
  MeiliSuggestedThought,
  MindstringPrototype,
} from "@/generated/fastapi";
import {
  showErrorToast,
  showSuccessToast,
} from "@/shared/feedback";
import { useUserChoices } from "@/state/globalStore";
import { useSuggestedThoughtConfirmationContext } from "../../contexts/SuggestedThoughtConfirmationProvider";
import useSuggestedThoughtActions from "../../hooks/useSuggestedThoughtActions";
import useThoughtRefresh from "../../hooks/useThoughtRefresh";
import useSuggestedThoughtsReviewQuery from "./useSuggestedThoughtsReviewQuery";

type UseSuggestedThoughtsReviewControllerParams = {
  enabled?: boolean;
  languageFilters?: string[];
  limit?: number;
  originId?: string | null;
  prototype: MindstringPrototype;
  queryText?: string;
  semanticRatio?: number;
  situationId?: string | null;
  sortBy?: string;
};

export default function useSuggestedThoughtsReviewController({
  enabled = true,
  languageFilters,
  limit,
  originId,
  prototype,
  queryText,
  semanticRatio,
  situationId = null,
  sortBy,
}: UseSuggestedThoughtsReviewControllerParams) {
  const {
    removeSelectedSuggestedThoughts,
    selectedSuggestedThoughts,
    toggleAllSelectedSuggestedThoughts,
  } = useUserChoices();
  const {
    studioSuggestedThoughtsRefreshStamp,
    syncSuggestedThoughtMutation,
  } = useThoughtRefresh();
  const {
    confirmSingleThoughtConfirmation,
    setConfirmationPrototypes,
  } = useSuggestedThoughtConfirmationContext();
  const {
    handleSuggestedThoughtsConfirmClick,
    handleSuggestedThoughtsDeleteClick,
  } = useSuggestedThoughtActions({
    confirmSuggestedThoughtConfirmation: confirmSingleThoughtConfirmation,
    setConfirmSuggestedThoughtPrototypes: setConfirmationPrototypes,
  });

  const suggestedThoughtsQuery = useSuggestedThoughtsReviewQuery({
    enabled,
    languageFilters,
    limit: limit ? limit + 1 : undefined,
    originId,
    prototype,
    queryText,
    refreshKey: studioSuggestedThoughtsRefreshStamp[prototype],
    semanticRatio,
    situationId,
    sortBy,
  });
  const rawSuggestions = useMemo(
    () => suggestedThoughtsQuery.data ?? [],
    [suggestedThoughtsQuery.data],
  );
  const suggestions = useMemo(
    () => (limit ? rawSuggestions.slice(0, limit) : rawSuggestions),
    [limit, rawSuggestions],
  );
  const canLoadMoreSuggestions = limit
    ? rawSuggestions.length > limit
    : false;
  const visibleSuggestedThoughtIds = useMemo(
    () => suggestions.map((thought) => thought.objectID),
    [suggestions],
  );
  const selectedVisibleSuggestedThoughtIds = useMemo(
    () =>
      selectedSuggestedThoughts.filter((thoughtId) =>
        visibleSuggestedThoughtIds.includes(thoughtId),
      ),
    [selectedSuggestedThoughts, visibleSuggestedThoughtIds],
  );
  const selectedSuggestedThoughtCount =
    selectedVisibleSuggestedThoughtIds.length;
  const canSelectAllSuggestedThoughts =
    selectedSuggestedThoughtCount < suggestions.length;
  const handleSuggestedThoughtReviewSync = useCallback(() => {
    syncSuggestedThoughtMutation(prototype);
  }, [prototype, syncSuggestedThoughtMutation]);

  const handleConfirmSelectedSuggestedThoughts = useCallback(async () => {
    await handleSuggestedThoughtsConfirmClick({
      request: {
        thought_ids: selectedVisibleSuggestedThoughtIds,
      },
      onSuccess: () => {
        removeSelectedSuggestedThoughts(selectedVisibleSuggestedThoughtIds);
        syncSuggestedThoughtMutation(prototype);
      },
    });
  }, [
    handleSuggestedThoughtsConfirmClick,
    prototype,
    removeSelectedSuggestedThoughts,
    selectedVisibleSuggestedThoughtIds,
    syncSuggestedThoughtMutation,
  ]);

  const handleDeleteSelectedSuggestedThoughts = useCallback(async () => {
    await handleSuggestedThoughtsDeleteClick(
      {
        thought_ids: selectedVisibleSuggestedThoughtIds,
        delete_mode: "permanent",
        sync_meili_index: "suggested_thoughts",
      },
      () => {
        syncSuggestedThoughtMutation(prototype);
        removeSelectedSuggestedThoughts(selectedVisibleSuggestedThoughtIds);
      },
      [prototype],
    );
  }, [
    handleSuggestedThoughtsDeleteClick,
    prototype,
    removeSelectedSuggestedThoughts,
    selectedVisibleSuggestedThoughtIds,
    syncSuggestedThoughtMutation,
  ]);

  const handleCopySelectedSuggestedThoughts = useCallback(async () => {
    const selectedThoughts = suggestions
      .filter((thought) =>
        selectedVisibleSuggestedThoughtIds.includes(thought.objectID),
      )
      .map((thought) => (thought as MeiliSuggestedThought).mindstring?.trim())
      .filter((mindstring): mindstring is string => Boolean(mindstring));

    if (selectedThoughts.length === 0) {
      showErrorToast("Select suggestions to copy");
      return;
    }

    try {
      await navigator.clipboard.writeText(selectedThoughts.join("\n"));
      showSuccessToast("Copied selected suggestions to clipboard");
    } catch {
      showErrorToast("Failed to copy suggestions");
    }
  }, [selectedVisibleSuggestedThoughtIds, suggestions]);

  const handleSelectAllSuggestedThoughts = useCallback(() => {
    toggleAllSelectedSuggestedThoughts(visibleSuggestedThoughtIds);
  }, [toggleAllSelectedSuggestedThoughts, visibleSuggestedThoughtIds]);

  const handleUnselectSelectedSuggestedThoughts = useCallback(() => {
    removeSelectedSuggestedThoughts(selectedVisibleSuggestedThoughtIds);
  }, [removeSelectedSuggestedThoughts, selectedVisibleSuggestedThoughtIds]);

  return {
    canLoadMoreSuggestions,
    canSelectAllSuggestedThoughts,
    handleConfirmSelectedSuggestedThoughts,
    handleCopySelectedSuggestedThoughts,
    handleDeleteSelectedSuggestedThoughts,
    handleSelectAllSuggestedThoughts,
    handleSuggestedThoughtReviewSync,
    handleUnselectSelectedSuggestedThoughts,
    isLoadingSuggestions: suggestedThoughtsQuery.isLoading,
    selectedSuggestedThoughtCount,
    suggestions,
  };
}
